'use client';

import { FC } from 'react';

import { OrderBaseUrls, OrderDocumentResponse, ShipmentDetail, TextData } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { useFormatter } from '@ravago/shared/translations/client';

import { BusinessContextUtils } from '../../../../utils/business-context/business-context.utils';
import { OrderDocuments } from '../../../shared/order-documents/order-documents';
import { OrderPageHeader } from '../../../shared/order-page-header/order-page-header';
import { DeliveryInformation } from '../../../shared/order/delivery-information';
import { InvoiceInformation } from '../../../shared/order/invoice-information';

import { ShipmentProducts } from './shipment-products';

export type Props = {
   globals: RendererGlobals;
   orderBaseUrls: OrderBaseUrls;
   shipment: ShipmentDetail;
   shipmentId: string;
   hasDocuments: boolean;
   showInvoiceDocuments: boolean;
   titleData: TextData;
   shipmentDocuments: OrderDocumentResponse | undefined;
   showVatCode: boolean;
};

export const ShipmentDetails: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      businessContext,
      isConsumer,
      assetBasePath,
      actions: { requestBol, requestCoa, requestDeliveryNote, downloadDocument }
   },
   titleData,
   shipment,
   shipmentId,
   orderBaseUrls,
   hasDocuments,
   showInvoiceDocuments,
   shipmentDocuments,
   showVatCode
}) => {
   const format = useFormatter();

   return (
      <Box direction="column">
         <OrderPageHeader
            headerText={titleData}
            id={shipmentId}
            status={shipment.status}
            date={DateUtils.transformOrderDeliveryDate(format, shipment.deliveryDate)}
         />
         <ShipmentProducts
            orders={shipment.orders}
            locale={locale}
            isConsumer={isConsumer}
            acceptWebp={acceptWebp}
            assetBasePath={assetBasePath}
            orderBaseUrls={orderBaseUrls}
            showInvoiceDocuments={showInvoiceDocuments}
            requestBol={requestBol}
            requestCoa={requestCoa}
            requestDeliveryNote={requestDeliveryNote}
            downloadDocument={downloadDocument}
            isUSBusinessContext={BusinessContextUtils.isUSBusinessContext(businessContext)}
         />
         <Divider color="default" orientation="horizontal" />
         <DeliveryInformation
            destinationAddress={shipment.destinationAddress}
            deliveryTermAddress={shipment.deliveryTermAddress}
            deliveryInstructions={shipment.deliveryInstructions}
            deliveryTerm={shipment.deliveryTerm}
         />
         <Divider color="default" orientation="horizontal" />
         <InvoiceInformation vatCode={shipment.vatCode} invoiceEntity={shipment.ole} showVatCode={showVatCode} />
         <Divider color="default" orientation="horizontal" />
         {hasDocuments && (
            <OrderDocuments
               isUSBusinessContext={BusinessContextUtils.isUSBusinessContext(businessContext)}
               documents={shipmentDocuments}
               shipmentOrder={shipment.orders}
               showInvoiceDocuments={showInvoiceDocuments}
               requestCoa={requestCoa}
               requestBol={requestBol}
               requestDeliveryNote={requestDeliveryNote}
               downloadDocument={downloadDocument}
            />
         )}
      </Box>
   );
};
